import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import { closeRightModal } from "../../../REDUX/actions/rightModal";
import { createUpdateCommercial } from "../../../REDUX/actions/commercials";
import { commercialForm } from "./data";
import { usePartnerOrganizations } from "../usePartnerOrganizations";

// Same shape as CompetitorModal (Informations tab only).
const CommercialModal = ({ commercial }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const isEdit = !!commercial;
  const attributes = commercial?.attributes ?? {};
  // Its SOFT access: the password typed here creates it, or changes it.
  const hasAccess = !!attributes.user?.data;

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [password, setPassword] = useState("");

  // Admin: any store (optional); business affiliate: one it sponsors
  // (required); reseller: pinned server-side to its own store.
  const { isAffiliate, canPickOrganization, organizationOptions } =
    usePartnerOrganizations();
  const { scope } = useSelector((state) => state.appCustomersReducer);

  const [formData, setFormData] = useState(() => {
    const initial = {};
    commercialForm.forEach((el) => {
      initial[el.name] = attributes[el.name] ?? "";
    });
    const organizationId =
      attributes.organization?.data?.id ?? (isEdit ? null : scope.organizationId);
    initial.organization = organizationId ? { code: organizationId } : null;
    return initial;
  });

  // The access's login is the email; same minimum as a customer's password.
  const passwordError = !password
    ? null
    : !String(formData.email ?? "").trim()
      ? "Add the commercial's email first"
      : password.length < 6
        ? "PASSWORD_TOO_SHORT"
        : null;

  // Select matches its value by equality — use the loaded option object.
  useEffect(() => {
    if (!formData.organization?.code || !organizationOptions.length) return;
    const match = organizationOptions.find(
      (o) => o.code === formData.organization.code
    );
    if (match && match !== formData.organization) {
      setFormData((prev) => ({ ...prev, organization: match }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const checkFormValidation = useCallback(() => {
    const invalid =
      commercialForm.some(
        ({ name, required }) => required && !String(formData[name] ?? "").trim()
      ) ||
      (isAffiliate && !formData.organization?.code) ||
      !!passwordError;
    setDisabled(invalid);
  }, [formData, isAffiliate, passwordError]);

  useEffect(() => {
    checkFormValidation();
  }, [checkFormValidation]);

  const handleSave = () => {
    setLoading(true);
    dispatch(
      createUpdateCommercial(
        formData,
        commercial?.id,
        t,
        () => dispatch(closeRightModal()),
        password
      )
    ).then(() => setLoading(false));
  };

  return (
    <div className="p-4 d-flex flex-column h-100">
      <h2 className="rightModalTitle quickSand bold text-blue mb-4">
        {isEdit ? t("Modify a commercial") : t("Add a commercial")}
      </h2>
      <div className="row">
        {commercialForm.map((el) => (
          <div className="col-6 mt-4" key={el?.name}>
            <p className="f16 text-ultraBlack mb-2">
              {t(`${el?.label}`)}
              {el?.required ? <span className="text-red"> *</span> : null}
            </p>
            <TextInput
              {...el}
              value={formData[el?.name]}
              callback={handleChange}
            />
          </div>
        ))}
        {canPickOrganization ? (
          <div className="col-6 mt-4">
            <p className="f16 text-ultraBlack mb-2">
              {t("Reseller")}
              {isAffiliate ? <span className="text-red"> *</span> : null}
            </p>
            <Select
              name="organization"
              value={formData.organization}
              options={organizationOptions}
              callback={handleChange}
              filter
              showClear
              appendToSelf
            />
          </div>
        ) : null}
        <div className="col-6 mt-4">
          <p className="f16 text-ultraBlack mb-2">{t("Password")}</p>
          <TextInput
            type="password"
            name="password"
            value={password}
            callback={(e) => setPassword(e.target.value)}
            placeholder={
              hasAccess
                ? t("Leave empty to keep unchanged")
                : t("Fill in to give the access (login: the email)")
            }
          />
          {passwordError ? (
            <p className="f12 text-red mt-1 mb-0">{t(passwordError)}</p>
          ) : null}
        </div>
      </div>
      <div className="d-flex justify-content-end mt-5">
        <Button
          variant="outline"
          className="me-4"
          onClick={() => dispatch(closeRightModal())}
        >
          {t("Cancel")}
        </Button>
        <Button
          loading={loading}
          disabled={disabled}
          onClick={loading ? undefined : handleSave}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default CommercialModal;
